import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import EventStatusChip from "../events/EventStatusChip";
import { formatDateTime } from "../../utils/date";
import {
  closeSourceDetailDialog,
  fetchSourceChannelDetail,
  selectSourceChannelById,
  selectSourceChannelDetail,
  selectSourceDetailDialogId,
} from "../../store/slices/sourceChannelsSlice";

const Field = ({ label, value }) => (
  <Box sx={{ mb: 2 }}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Box sx={{ mt: 0.5 }}>
      {typeof value === "string" || typeof value === "number" ? (
        <Typography variant="body1" fontWeight={600}>
          {value}
        </Typography>
      ) : (
        value
      )}
    </Box>
  </Box>
);

const SourceChannelDetailDialog = () => {
  const dispatch = useDispatch();
  const channelId = useSelector(selectSourceDetailDialogId);
  const channel = useSelector((state) => selectSourceChannelById(state, channelId));
  const detailState = useSelector(selectSourceChannelDetail);
  const [showSecret, setShowSecret] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (channelId && !channel) {
      dispatch(fetchSourceChannelDetail(channelId));
    }
  }, [dispatch, channelId, channel]);

  const handleClose = () => dispatch(closeSourceDetailDialog());
  const secretValue = channel?.secret_key || "";
  const maskedSecret = secretValue ? "*".repeat(secretValue.length) : "-";

  const handleToggleSecret = () => {
    setShowSecret((prev) => !prev);
  };

  const handleCopySecret = async () => {
    if (!secretValue) return;
    try {
      await navigator.clipboard.writeText(secretValue);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignore clipboard errors
    }
  };

  return (
    <Dialog open={Boolean(channelId)} onClose={handleClose} maxWidth="sm" fullWidth>
      {channelId && (
        <>
          <DialogTitle
            sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
          >
            <Box>
              <Typography variant="h6">{channel?.name || "Source Channel"}</Typography>
              <Typography variant="body2" color="text.secondary">
                Source Channel #{channel?.id || channelId}
              </Typography>
            </Box>
            <IconButton onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            {!channel && (
              <Typography variant="body2" color="text.secondary">
                {detailState.loading
                  ? "Loading source channel details..."
                  : detailState.error || "Source channel details are unavailable."}
              </Typography>
            )}
            {channel && (
              <>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Field label="Name" value={channel.name} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Field label="Status" value={<EventStatusChip status={channel.status} />} />
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        Secret Key
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                        <Typography variant="body1" fontWeight={600}>
                          {showSecret ? secretValue || "-" : maskedSecret}
                        </Typography>
                        <Tooltip title={showSecret ? "Hide secret key" : "Show secret key"}>
                          <span>
                            <IconButton size="small" onClick={handleToggleSecret}>
                              {showSecret ? (
                                <VisibilityOffIcon fontSize="small" />
                              ) : (
                                <VisibilityIcon fontSize="small" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title={copied ? "Copied!" : "Copy secret key"}>
                          <span>
                            <IconButton
                              size="small"
                              onClick={handleCopySecret}
                              disabled={!secretValue}
                            >
                              <ContentCopyIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Field label="Created" value={formatDateTime(channel.created_datetime)} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Field label="Updated" value={formatDateTime(channel.updated_datetime)} />
                  </Grid>
                </Grid>
              </>
            )}
          </DialogContent>
        </>
      )}
    </Dialog>
  );
};

export default SourceChannelDetailDialog;
